import Tooltip from "@ui-kit/overlays/Tooltip/Tooltip.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Icon from "@shared/components/ui-kit/icons/Icon.web";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import {
  getStaticEventDateRange,
  IHasStartAndEnd,
  isMultiDayEvent,
} from "@shared/modules/events/utils/event-utils";
import { getPriceRange } from "@shared/modules/financials/utils/price-utils";
import { SeatingEventPropertyMap } from "@shared/modules/seating/types/seating-property-types";
import { useTranslation } from "@shared/utils/internationalization";
import { formatDate } from "@shared/utils/dates/formatting";
import { useRouter } from "next/router";
import { Dispatch, SetStateAction, useMemo } from "react";

export interface EventBadgesProps {
  /** @deprecated No longer used -- responsive switching is CSS-based now */
  isSmallerThanSm?: boolean;
  eventData: PublicBaseEventSerializer | SeatingEventPropertyMap;
  eventTabOptions?: { id: number; title?: string; hash?: string }[] | null;
  selectedTab?: number | null;
  setSelectedTab?: Dispatch<SetStateAction<number>> | null;
  handleViewMap?: ((input: boolean) => void) | null;
  isAttractionPage?: boolean;
  condensed?: boolean;
}

type EventBadge = {
  id: number;
  icon: JSX.Element;
  text: JSX.Element;
  showBadge: boolean;
  dataTestId: string;
};

/**
 * TODO: Technical Debt - Refactor this component to use a proper React component pattern.
 *
 * EventBadges - Legacy Function Component (Anti-pattern)
 *
 * ⚠️  WARNING: This component violates React's Rules of Hooks ⚠️
 *
 * This function is named like a React component but is called as a regular function
 * throughout the codebase. It uses hooks (useRouter, useTranslation, useMemo) which
 * technically violates React's rules, but works because it's always called from within
 * React components in a consistent manner.
 *
 * WHY THIS EXISTS:
 * - Legacy code from 3+ years ago
 * - Used in 5+ places across the codebase
 * - Returns badge objects instead of JSX for flexible rendering
 *
 * PROPER PATTERNS (for new code):
 * - Custom hook: `useEventBadges()` - returns badge data
 * - React component: `<EventBadges />` - renders JSX directly
 * - Compound components: `<EventBadges.Date />`, `<EventBadges.Time />`
 *
 * REFACTOR DECISION:
 * - Works reliably for 3+ years
 * - Test coverage exists
 * - Refactor effort doesn't justify user value (0%)
 * - Marked as "technical debt" but not actively harmful
 *
 * DO NOT COPY THIS PATTERN FOR NEW COMPONENTS!
 */
const EventBadges = ({
  isSmallerThanSm: _isSmallerThanSm,
  eventData,
  eventTabOptions: _eventTabOptions,
  selectedTab: _selectedTab,
  setSelectedTab: _setSelectedTab,
  handleViewMap,
  isAttractionPage,
  condensed = false,
}: EventBadgesProps): EventBadge[] => {
  const { t } = useTranslation("common");

  const router = useRouter();

  const priceRangeString = useMemo(() => {
    let priceRange = "";

    if (eventData) {
      priceRange = getPriceRange({
        items: eventData?.ticket_types,
        allInPricing: eventData?.venue?.all_in_pricing_is_enabled,
        venueCurrency: eventData?.venue?.currency,
        shouldRound: false,
      });
    }

    return priceRange;
  }, [
    eventData?.ticket_types,
    eventData?.venue?.all_in_pricing_is_enabled,
    eventData?.venue?.currency,
  ]);

  const abbreviatedTimeFormat = router.locale === "en" ? "h:mm A" : "HH:mm[h]";
  const fullTimeFormat = router.locale === "en" ? `h:mm A z` : `HH:mm[h] z`;

  // Date format constants for multi-day events
  const mobileMultiDayFormat =
    router.locale === "en"
      ? "MMM Do, YYYY @ h:mm A z"
      : "Do MMMM, YYYY @ HH:mm[h] z";

  const desktopMultiDayFormat =
    router.locale === "en"
      ? "dddd MMM Do, YYYY @ h:mm A z"
      : "dddd Do MMMM, YYYY @ HH:mm[h] z";

  // Opens the map modal when View Map is clicked
  const displayMapToUser = (): void => {
    handleViewMap?.(true);
  };

  const getLocationComponent = (): JSX.Element => {
    // display text if event address does not exist
    let returnElement = <Text>Location Unavailable</Text>;

    // display text for online events
    if (eventData?.virtual_event_type) {
      returnElement = <Text>Online Event</Text>;
    }
    // display the event's address if it exists
    else if (
      eventData?.location?.name &&
      eventData?.location?.street_name &&
      eventData?.location?.city
    ) {
      returnElement = (
        <Flex direction="column">
          {condensed ? (
            <Text>
              {`${eventData?.location?.name}, ${eventData?.location?.street_name}, ${eventData?.location?.city}`}
            </Text>
          ) : (
            <>
              <Text>{eventData?.location?.name}</Text>
              <Text>{` ${eventData?.location?.street_name}, ${eventData?.location?.city}`}</Text>
            </>
          )}
          <Text
            hidden={!handleViewMap}
            tabIndex={0}
            role="button"
            width="fit-content"
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                displayMapToUser();
              }
            }}
            onClick={() => {
              displayMapToUser();
            }}
            cursor="pointer"
            color="secondary.fg"
            textDecoration="underline"
          >
            View Map
          </Text>
        </Flex>
      );
    }

    return returnElement;
  };

  const getTimeComponent = (): JSX.Element => {
    const startTimeAbbreviated = formatDate({
      date: eventData?.starts_on,
      timezone: eventData?.timezone,
      format: abbreviatedTimeFormat,
    });
    const startTimeFull = formatDate({
      date: eventData?.starts_on,
      timezone: eventData?.timezone,
      format: fullTimeFormat,
    });
    const endTime = formatDate({
      date: eventData?.ends_on,
      timezone: eventData?.timezone,
      format: fullTimeFormat,
    });

    return (
      <>
        {/* Mobile: single line "9:00 PM - 2:00 AM MDT" */}
        <Flex direction="row" gap={1} display={{ base: "flex", sm: "none" }}>
          <Text>{startTimeAbbreviated}</Text>
          <Text>-</Text>
          <Text>{endTime}</Text>
        </Flex>
        {/* Desktop: labeled "Starts/Ends" format */}
        <Flex direction="column" display={{ base: "none", sm: "flex" }}>
          {condensed ? (
            <Text>
              <Text as="span" fontWeight="bold">
                {t("Starts")}:
              </Text>{" "}
              {startTimeFull} |{" "}
              <Text as="span" fontWeight="bold">
                {t("Ends")}:
              </Text>{" "}
              {endTime}
            </Text>
          ) : (
            <>
              <Text>
                <Text as="span" fontWeight="bold">
                  {t("Starts")}:
                </Text>{" "}
                {startTimeFull}
              </Text>
              <Text>
                <Text as="span" fontWeight="bold">
                  {t("Ends")}:
                </Text>{" "}
                {endTime}
              </Text>
            </>
          )}
        </Flex>
      </>
    );
  };

  const getDateComponent = (): JSX.Element => {
    const fullDateFormat =
      router.locale === "en" ? "dddd MMMM Do, YYYY" : "dddd D MMMM, YYYY";

    const mobileDate = getStaticEventDateRange(
      eventData as IHasStartAndEnd,
      "TBD"
    );

    const desktopDate = eventData?.date_time_to_be_determined
      ? "TBD"
      : formatDate({
          date: eventData?.starts_on,
          timezone: eventData?.timezone,
          format: fullDateFormat,
        });

    return (
      <>
        {/* Mobile: compact date range */}
        <Box display={{ base: "block", sm: "none" }}>
          <Text>{mobileDate}</Text>
        </Box>
        {/* Desktop: full date */}
        <Box display={{ base: "none", sm: "block" }}>
          <Text>{desktopDate}</Text>
        </Box>
      </>
    );
  };

  const getStartDateTimeComponent = (): JSX.Element => {
    const mobileStart = formatDate({
      date: eventData?.starts_on,
      timezone: eventData?.timezone,
      format: mobileMultiDayFormat,
    });
    const desktopStart = formatDate({
      date: eventData?.starts_on,
      timezone: eventData?.timezone,
      format: desktopMultiDayFormat,
    });

    return (
      <Tooltip
        label={t("Please check for daily hours of operation")}
        placement="top"
      >
        <Box>
          <Box textAlign="left" display={{ base: "block", sm: "none" }}>
            <Text fontWeight="bold">{t("Starts")}</Text> {mobileStart}
          </Box>
          <Box textAlign="left" display={{ base: "none", sm: "block" }}>
            <Text fontWeight="bold">{t("Starts")}</Text> {desktopStart}
          </Box>
        </Box>
      </Tooltip>
    );
  };

  const getEndDateTimeComponent = (): JSX.Element => {
    if (eventData?.date_time_to_be_determined) {
      return <Text>TBD</Text>;
    }

    const mobileEnd = formatDate({
      date: eventData?.ends_on,
      timezone: eventData?.timezone,
      format: mobileMultiDayFormat,
    });
    const desktopEnd = formatDate({
      date: eventData?.ends_on,
      timezone: eventData?.timezone,
      format: desktopMultiDayFormat,
    });

    return (
      <Tooltip
        label={t("Please check for daily hours of operation")}
        placement="top"
      >
        <Box>
          <Box textAlign="left" display={{ base: "block", sm: "none" }}>
            <Text fontWeight="bold">{t("Ends")}</Text> {mobileEnd}
          </Box>
          <Box textAlign="left" display={{ base: "none", sm: "block" }}>
            <Text fontWeight="bold">{t("Ends")}</Text> {desktopEnd}
          </Box>
        </Box>
      </Tooltip>
    );
  };

  const getTicketPriceRangeText = () => {
    return (
      <Text>{eventData?.no_ticket_types ? t("Free") : priceRangeString}</Text>
    );
  };

  const isMultiDay = isMultiDayEvent(eventData as IHasStartAndEnd);

  const badges: EventBadge[] = [];

  // For multi-day events, create separate start and end badges
  if (isMultiDay) {
    if (eventData?.date_time_to_be_determined) {
      // For TBD multi-day events, show only one TBD badge
      badges.push({
        id: 0,
        icon: (
          <Icon.Calendar
            mr="xs"
            colorPalette="primary"
            width="element.xl"
            height="element.xl"
          />
        ),
        text: <Text>TBD</Text>,
        showBadge: true,
        dataTestId: "date-tbd",
      });
    } else {
      // For non-TBD multi-day events, show separate start and end badges
      badges.push(
        {
          id: 0,
          icon: (
            <Icon.Calendar
              mr="xs"
              colorPalette="primary"
              width="element.xl"
              height="element.xl"
            />
          ),
          text: getStartDateTimeComponent(),
          showBadge: true,
          dataTestId: "start-date",
        },
        {
          id: 1,
          icon: (
            <Icon.Calendar
              mr="xs"
              colorPalette="primary"
              width="element.xl"
              height="element.xl"
            />
          ),
          text: getEndDateTimeComponent(),
          showBadge: true,
          dataTestId: "end-date",
        }
      );
    }
  } else {
    // For single-day events or small screens, use the original date badge
    badges.push({
      id: 0,
      icon: (
        <Icon.Calendar
          mr="xs"
          colorPalette="primary"
          width="element.xl"
          height="element.xl"
        />
      ),
      text: getDateComponent(),
      showBadge: true,
      dataTestId: "date",
    });
  }

  badges.push({
    id: badges.length,
    icon: <Icon.Clock mr="xs" width="element.xl" height="element.xl" />,
    text: getTimeComponent(),
    showBadge:
      !eventData?.date_time_to_be_determined &&
      !eventData?.attraction_event_config &&
      !isMultiDay,
    dataTestId: "time",
  });

  // Add doors open badge
  badges.push({
    id: badges.length,
    icon: (
      <Icon.Login
        mr="xs"
        colorPalette="primary"
        width="element.xl"
        height="element.xl"
      />
    ),
    text: (
      <>
        <Text display={{ base: "block", sm: "none" }}>
          {`${t("Doors Open")}: ${formatDate({
            date: eventData?.opens_at,
            timezone: eventData?.timezone,
            format: abbreviatedTimeFormat,
          })}`}
        </Text>
        <Text display={{ base: "none", sm: "block" }}>
          {`${t("Doors Open")}: ${formatDate({
            date: eventData?.opens_at,
            timezone: eventData?.timezone,
            format: fullTimeFormat,
          })}`}
        </Text>
      </>
    ),
    showBadge:
      !!eventData?.opens_at &&
      !eventData?.date_time_to_be_determined &&
      !eventData?.is_recurring_parent,
    dataTestId: "doors-open",
  });

  // Add location badge
  badges.push({
    id: badges.length,
    icon: eventData?.virtual_event_type ? (
      <Icon.Link
        boxSize={{ base: "element.md", sm: "element.xl" }}
        m={{ base: "0 12px 0 0", sm: "2px 12px 0 0" }}
        colorPalette="primary"
        aria-label="website icon"
      />
    ) : (
      <Icon.Location
        mr="xs"
        colorPalette="primary"
        width="element.xl"
        height="element.xl"
      />
    ),
    text: getLocationComponent(),
    showBadge: true,
    dataTestId: "location",
  });

  // Add price badge
  badges.push({
    id: badges.length,
    icon: (
      <Icon.DollarCircle
        mr="xs"
        colorPalette="primary"
        // color="primary.600"
        width="element.xl"
        height="element.xl"
      />
    ),
    text: getTicketPriceRangeText(),
    showBadge: isAttractionPage ? false : Boolean(priceRangeString),
    dataTestId: "price",
  });

  return badges;
};

export default EventBadges;
